"use client";

/**
 * React hook returning settings-aware time/date formatters.
 *
 * Reads `Restaurant.settings` from the live `SelectionContext` Firestore
 * subscription (see `contexts/SelectionContext.tsx:152-231`). When the
 * restaurant's clockFormat / dateFormat / showSeconds is changed in
 * management (or in another tab), every component using this hook
 * rerenders automatically — no manual cache invalidation needed.
 *
 * For non-React code (utility functions, server-side rendering paths)
 * import the underlying pure functions from `../utils/timeFormatter`
 * directly and pass `settings` as a parameter.
 */

import { useMemo } from 'react';
import { useSelectedLocation } from '../contexts/SelectionContext';
import {
    formatDate,
    formatDateTime,
    formatTime,
    type TimeFormatSettings,
} from '../utils/timeFormatter';

export interface LocationTimeFormatter {
    formatTime: (date: Date) => string;
    formatDate: (date: Date) => string;
    formatDateTime: (date: Date) => string;
}

/**
 * @param settingsOverride Time/date settings to use directly. Public apps
 *   (ordering / checkout / restaurant) have no `SelectionProvider`, so they
 *   must pass the resolved restaurant's `settings` here to get accurate
 *   per-restaurant formatting. When omitted, the hook reads the live
 *   SelectionContext subscription (management) and falls back to platform
 *   defaults when no provider is mounted — never throwing.
 */
export function useTimeFormatter(
    settingsOverride?: TimeFormatSettings | null,
): LocationTimeFormatter {
    const { restaurant: locationRecord } = useSelectedLocation();
    const settings = settingsOverride ?? locationRecord?.settings;

    return useMemo<LocationTimeFormatter>(
        () => ({
            formatTime: (date) => formatTime(date, settings),
            formatDate: (date) => formatDate(date, settings),
            formatDateTime: (date) => formatDateTime(date, settings),
        }),
        // The three settings fields are the only inputs that affect formatting.
        // Listing them individually avoids invalidating the memo on every
        // unrelated restaurant-doc field change.
        [settings?.clockFormat, settings?.dateFormat, settings?.showSeconds],
    );
}
